import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { daoInvoicePayPost, daoProcessQuotePost } from 'dao/ucs-dao';
import { isEcomClient } from 'modules/session-details';
import { getRevenueTrackingType } from 'modules/tracking/tracking-module';
import { Thunk } from 'types/root';
import { ProcessQuotePayload, UCSState } from 'types/ucs';

export const postProcessQuote = createAsyncThunk<boolean, ProcessQuotePayload, Thunk>(
  'ucs/quote-process',
  async ({ quoteCode, payload }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedClientIsEcom = isEcomClient(state);
    const requestOriginValue = getRevenueTrackingType(selectedClientIsEcom, state.sessionDetails.cancelRedirectUrl);

    const response = await daoProcessQuotePost(state.globalWrapper.accountKey, quoteCode, payload, requestOriginValue);
    return !!response;
  },
);

export const postProcessInvoice = createAsyncThunk<boolean, { sessionId: string }, Thunk>(
  'ucs/invoice-pay',
  async ({ sessionId }, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedClientIsEcom = isEcomClient(state);
    const requestOriginValue = getRevenueTrackingType(selectedClientIsEcom, state.sessionDetails.cancelRedirectUrl);

    const response = await daoInvoicePayPost(state.globalWrapper.accountKey, sessionId, requestOriginValue);
    return !!response;
  },
);

const initialState: UCSState = {
  isProcessQuoteLoading: false,
  isProcessQuoteSuccessful: false,
  isProcessQuoteFailed: false,
  isProcessInvoiceFailed: false,
  isProcessInvoiceLoading: false,
  isProcessInvoiceSuccessful: false,
};

const ucsSlice = createSlice({
  name: 'ucs',
  initialState,
  reducers: {
    resetProcessQuoteLoaders(state) {
      state.isProcessQuoteFailed = false;
      state.isProcessQuoteLoading = false;
      state.isProcessQuoteSuccessful = false;
    },
    resetProcessInvoiceLoaders(state) {
      state.isProcessInvoiceFailed = false;
      state.isProcessInvoiceLoading = false;
      state.isProcessInvoiceSuccessful = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(postProcessQuote.pending, (state) => {
        state.isProcessQuoteLoading = true;
        state.isProcessQuoteFailed = false;
        state.isProcessQuoteSuccessful = false;
      })
      .addCase(postProcessQuote.fulfilled, (state) => {
        state.isProcessQuoteLoading = false;
        state.isProcessQuoteFailed = false;
        state.isProcessQuoteSuccessful = true;
      })
      .addCase(postProcessQuote.rejected, (state) => {
        state.isProcessQuoteFailed = true;
        state.isProcessQuoteLoading = false;
        state.isProcessQuoteSuccessful = false;
      })
      .addCase(postProcessInvoice.pending, (state) => {
        state.isProcessInvoiceLoading = true;
        state.isProcessInvoiceFailed = false;
        state.isProcessInvoiceSuccessful = false;
      })
      .addCase(postProcessInvoice.fulfilled, (state) => {
        state.isProcessInvoiceLoading = false;
        state.isProcessInvoiceFailed = false;
        state.isProcessInvoiceSuccessful = true;
      })
      .addCase(postProcessInvoice.rejected, (state) => {
        state.isProcessInvoiceFailed = true;
        state.isProcessInvoiceLoading = false;
        state.isProcessInvoiceSuccessful = false;
      });
  },
});

export const { resetProcessQuoteLoaders, resetProcessInvoiceLoaders } = ucsSlice.actions;

export default ucsSlice.reducer;
